import type { DiscoveryGetContentDetailsResponse } from "@amzn/ads-discovery-theme-service-typescript-client";
import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { TextButton } from "@amzn/storm-ui";
import { useAsync } from "@react-hookz/web";
import { isEmpty } from "lodash";
import type { FunctionComponent } from "react";
import { useCallback, useRef } from "react";
import { useStore } from "zustand";

import { ComplianceState } from "../../../../api/adsApi/adsApi.interfaces";
import { getDiscoveryApiClient } from "../../../../api/discoveryApi/discoveryApi";
import { INSIGHT_DETAILS_TRIGGER_TEXT } from "../../../../config/insights.config";
import { useInsightIdentifierEffect } from "../../../../hooks/misc/useInsightIdentifierEffect";
import { useAsyncErrorStatusNotification } from "../../../../hooks/notifications/useAsyncErrorStatusNotification";
import type { AssociatedPitchedContent } from "../../../../store/editContent";
import { editContentStore } from "../../../../store/editContent";
import { FeatureFlagsStore } from "../../../../store/featureFlags";
import { globalWorkbenchStore } from "../../../../store/globalWorkbench";
import { insightDetailsStore } from "../../../../store/insightDetails";
import { reportIssueStore } from "../../../../store/reportIssue";
import {
    defaultContentVersion,
    defaultExternalShareInfo,
    shareContentStore,
} from "../../../../store/shareContent";
import { AquilaThumbsFeedbackPlacement } from "../../../../types/aquila";
import type { CardDetail } from "../../../../types/card";
import { ContentPreviewType } from "../../../../types/content";
import {
    getInsightPreviewFileFormat,
    isMediaFile,
} from "../../../../utils/insightPreview.utils";
import { getAdvertiserId } from "../../../../utils/permission.utils";
import { AquilaThumbsFeedback } from "../../../aquilaThumbsFeedback/AquilaThumbsFeedback";
import { StyledActionsContainer } from "../../../common/styled.components";
import { InsightSelectionActionText } from "../InsightSelectionActionText";

export interface PitchedContentCardActionProps {
    details: CardDetail[];
    content: AssociatedPitchedContent;
    isEditable: boolean;
    isSharable: boolean;
}

export const PitchedContentCardAction: FunctionComponent<
    PitchedContentCardActionProps
> = ({ details, content, isEditable, isSharable }) => {
    const { contentId, fileFormat } = content;

    const [setEditContent, showEditModal] = useStore(
        editContentStore,
        (state) => [state.setContent, state.showEditModal],
    );
    const [thumbsFeedbackCard] = useStore(FeatureFlagsStore, (state) => [
        state.thumbsFeedbackCard,
    ]);

    const [getSlidesSelected] = useStore(globalWorkbenchStore, (state) => [
        state.getSlidesSelected,
    ]);

    const [setInsightDetails, setAsyncStatus, setDownloadInfo] = useStore(
        insightDetailsStore,
        (state) => [
            state.setInsightDetails,
            state.setAsyncStatus,
            state.setDownloadInfo,
        ],
    );

    const [updateReportIssueContextData] = useStore(
        reportIssueStore,
        (state) => [state.updateReportIssueContextData],
    );

    const [
        setExternalShareInfo,
        setIdentifier,
        setVersion,
        setShareModalOpen,
        setOriginalFileFormat,
    ] = useStore(shareContentStore, (state) => [
        state.setExternalShareInfo,
        state.setIdentifier,
        state.setVersion,
        state.setIsModalOpen,
        state.setOriginalFileFormat,
    ]);

    const contentDetailsRef = useRef<
        DiscoveryGetContentDetailsResponse | undefined
    >(undefined);

    const [contentAsyncState, contentAsyncActions] = useAsync(async () => {
        const data = await (
            await getDiscoveryApiClient()
        ).getContentDetails(contentId!, ContentPreviewType.PDF, false);

        const downloadData = await (
            await getDiscoveryApiClient()
        ).getContentDetails(contentId!, ContentPreviewType.ORIGINAL, true);

        const advertiserId = getAdvertiserId();

        const insightPreviewFileFormat = getInsightPreviewFileFormat(
            data.content?.fileFormat,
        );

        const compliantWorkbenchStatuses = [
            ComplianceState.COMPLIANT,
            ComplianceState.UNSUPPORTED_MEDIA_TYPE,
        ];

        const title = data.content?.contentTitle ?? "";

        updateReportIssueContextData({
            identifier: data.content?.contentId ?? "",
            title: title ?? "",
            insightType: InsightType.PITCHED_CONTENT,
        });

        contentDetailsRef.current = data;

        setInsightDetails({
            info: {
                title,
                identifier: data.content?.contentId ?? "",
                previewFileFormat: insightPreviewFileFormat,
                details,
                insightType: InsightType.PITCHED_CONTENT,
                previewAsset: {
                    downloadUrl: data.downloadUrl,
                    content: {
                        bucket: "",
                        key: "",
                    },
                },
                compliance: {
                    isCompliant: advertiserId
                        ? compliantWorkbenchStatuses.includes(
                              data.content
                                  ?.workbenchComplianceState as ComplianceState,
                          )
                        : false,
                    status: advertiserId
                        ? (data.content?.workbenchComplianceState ??
                          ComplianceState.UNSUPPORTED_PPTX)
                        : ComplianceState.UNSUPPORTED_VIEW,
                },
                ...(isEditable && { handleEditInsight }),
                ...(isSharable && { handleShareInsight }),
                isShareEnabled: !isMediaFile(insightPreviewFileFormat),
            },
            open: true,
            asyncStatus: contentAsyncState.status,
        });
        setDownloadInfo({
            downloadUrl: downloadData.downloadUrl,
            content: {
                bucket: "",
                key: "",
            },
            contentId: data.content?.contentId ?? "",
            contentVersion: data.content?.latestVersion
                ? data.content?.latestVersion.toString()
                : "1",
        });
        return data;
    });

    useAsyncErrorStatusNotification(contentAsyncState);

    const handleEditInsight = () => {
        setEditContent(content);
        showEditModal();
    };

    const handleShareInsight = () => {
        const contentDetails = contentDetailsRef.current;
        const externalShareDate =
            contentDetails!.content?.externalShareExpiryDate;
        const validExternalShare =
            externalShareDate && new Date(externalShareDate) > new Date();
        setExternalShareInfo({
            ...defaultExternalShareInfo,
            ...(validExternalShare && contentDetails!.content),
        });
        setVersion(
            contentDetails!.content?.latestVersion ?? defaultContentVersion,
        );
        setIdentifier(contentId!);
        setOriginalFileFormat(fileFormat!);
        setShareModalOpen(true);
    };

    const numSelectedSlides = getSlidesSelected(contentId!).length;

    const handleClick = useCallback(async () => {
        setAsyncStatus("loading");
        void contentAsyncActions.execute();
    }, []);

    useInsightIdentifierEffect(contentId!, handleClick);

    return (
        <StyledActionsContainer>
            <TextButton
                disabled={isEmpty(contentId)}
                onClick={() => void handleClick()}
            >
                {INSIGHT_DETAILS_TRIGGER_TEXT}
            </TextButton>
            {numSelectedSlides > 0 && (
                <InsightSelectionActionText
                    identifier={contentId!}
                    numSelectedSlides={numSelectedSlides}
                />
            )}
            {numSelectedSlides === 0 && thumbsFeedbackCard && (
                <AquilaThumbsFeedback
                    identifier={contentId!}
                    insightType={InsightType.PITCHED_CONTENT}
                    placement={AquilaThumbsFeedbackPlacement.CARD}
                />
            )}
        </StyledActionsContainer>
    );
};
